// Ações simples: posicionamento, remanejamento, loot e poderes de vocação.
import { ownedTerritories, vocationOf } from '../selectors';
import { addEffect } from './effects';
import type { PlayerId, TerritoryId } from '../types';
import type { Ctx } from './context';
import { finishTurn, startRound } from './turn';

export function placeSetup(ctx: Ctx, p: PlayerId, territory: TerritoryId, count: number): void {
  const { s } = ctx;
  const setup = s.setup!;
  s.territories[territory].troops += count;
  setup.remaining[p] -= count;
  setup.placedThisGo += count;
  ctx.events.push({ type: 'setupPlaced', player: p, territory, count });
  if (setup.placedThisGo >= s.info.ruleset.setupBatch || setup.remaining[p] === 0) advanceSetup(ctx);
}

export function autoSetup(ctx: Ctx, p: PlayerId): void {
  const { s, rng } = ctx;
  const setup = s.setup!;
  const owned = ownedTerritories(s, p);
  const placed: Record<TerritoryId, number> = {};
  for (let i = 0; i < setup.remaining[p]; i++) {
    const t = rng.pick(owned);
    placed[t] = (placed[t] ?? 0) + 1;
  }
  for (const [t, n] of Object.entries(placed)) {
    s.territories[t].troops += n;
    ctx.events.push({ type: 'setupPlaced', player: p, territory: t, count: n });
  }
  setup.remaining[p] = 0;
  advanceSetup(ctx);
}

function advanceSetup(ctx: Ctx): void {
  const { s } = ctx;
  const setup = s.setup!;
  setup.placedThisGo = 0;
  const n = s.players.length;
  for (let i = 1; i <= n; i++) {
    const idx = (s.current + i) % n;
    if (setup.remaining[idx] > 0) {
      s.current = idx;
      return;
    }
  }
  s.setup = null;
  ctx.events.push({ type: 'setupDone' });
  startRound(ctx);
}

export function placeReinforcement(ctx: Ctx, p: PlayerId, territory: TerritoryId, count: number): void {
  const { s } = ctx;
  s.territories[territory].troops += count;
  s.reinforcements -= count;
  ctx.events.push({ type: 'placed', player: p, territory, count });
}

export function fortify(ctx: Ctx, p: PlayerId, from: TerritoryId, to: TerritoryId, count: number): void {
  const { s } = ctx;
  s.territories[from].troops -= count;
  s.territories[to].troops += count;
  s.turn.movedIn[to] = (s.turn.movedIn[to] ?? 0) + count;
  ctx.events.push({ type: 'fortify', player: p, from, to, count });
}

/** Poder de dano: remove 1 tropa de um território que vai ser atacado. */
export function usePower(ctx: Ctx, p: PlayerId, target: TerritoryId): void {
  const { s } = ctx;
  s.territories[target].troops -= 1;
  s.turn.powerUsed = true;
  ctx.events.push({ type: 'power', player: p, target, owner: s.territories[target].owner });
}

/** Rajada de harmonia: gasta tudo; +1 no maior dado no turno e, com o máximo, perde no máximo 1 por combate. */
export function useHarmony(ctx: Ctx, p: PlayerId): void {
  const { s } = ctx;
  const me = s.players[p];
  const spent = me.harmony;
  me.harmony = 0;
  const guard = spent >= s.info.ruleset.harmonyMax;
  addEffect(ctx, 'flow', p, { at: 'turnEnd', player: p });
  if (guard) addEffect(ctx, 'flowGuard', p, { at: 'turnEnd', player: p });
  ctx.events.push({ type: 'harmony', player: p, spent, guard });
}

/** Harmonia: cada combate em que a vocação de harmonia ataca rende 1 (com limite por turno e no total). */
export function gainHarmony(ctx: Ctx, p: PlayerId): void {
  const { s } = ctx;
  const rs = s.info.ruleset;
  if (vocationOf(s, p) !== 'harmony') return;
  const me = s.players[p];
  if (s.turn.harmonyGained >= rs.harmonyPerTurn || me.harmony >= rs.harmonyMax) return;
  me.harmony++;
  s.turn.harmonyGained++;
}

/** Cura: 1 tropa num território próprio no fim do turno. */
export function heal(ctx: Ctx, p: PlayerId, territory: TerritoryId): void {
  const { s } = ctx;
  s.territories[territory].troops += 1;
  s.pending = null;
  ctx.events.push({ type: 'heal', player: p, territory });
  finishTurn(ctx);
}
